/** RT 再表示チェーンで特定 KW 該当時に上限を 4 件にするキーワード */
export const RT_REPEAT_KEYWORDS = ['施工管理', '自動車整備士', '電気主任技術者', '薬剤師', '介護士', '看護師']

/** 通常時の合計表示上限（初回 + 追加1） */
export const RT_TOTAL_DISPLAY_MAX_DEFAULT = 2

/** 特定 KW 該当時の合計表示上限（初回 + 追加3） */
export const RT_TOTAL_DISPLAY_MAX_KW = 4

/** 件数上限付きチェーンを 1 件進める契機（未送信 close） */
export type RtRepeatChainTrigger = 'close-without-submit'

/** 送客同意後サブボタン等、件数上限なしで次 RT を出す契機 */
export type RtOtherJobPopupTrigger = 'request-other-job'

export const RT_SUB_ACCEPT_LABEL_DEFAULT = '同意のみする'
export const RT_SUB_ACCEPT_LABEL_AFTER_SEND = '他の仕事紹介も希望する'

export type RtRepeatChainAdvanceResult = 'continued' | 'ended' | 'skipped'
export type RtOtherJobPopupResult = 'shown' | 'skipped'

type GlobalVarRepeat = {
  count_repeat: number
  checkRepeatRTChainExtendedKeywords: (
    formatId: string,
    formatData: object | null | undefined,
    keywordlist: string[],
    shokumuFromOther?: object | null
  ) => boolean
}

export function getRtRepeatTotalDisplayMax (
  globalVar: GlobalVarRepeat,
  formatId: string,
  formatData: object | null | undefined,
  shokumuFromOther?: object | null
): number {
  const hasRepeatKW = globalVar.checkRepeatRTChainExtendedKeywords(
    formatId,
    formatData,
    RT_REPEAT_KEYWORDS,
    shokumuFromOther
  )
  return hasRepeatKW ? RT_TOTAL_DISPLAY_MAX_KW : RT_TOTAL_DISPLAY_MAX_DEFAULT
}

export function restoreRtConditionAfterRepeatEnd<T extends object> (
  disableRtCondition4Repeat: T | null | undefined,
  applyPending: (conditionSet: T) => T
): T | undefined {
  if (!disableRtCondition4Repeat) return undefined
  const restored = JSON.parse(JSON.stringify(disableRtCondition4Repeat)) as T
  return applyPending(restored)
}

export interface CreateRtPopupRepeatChainOptions {
  globalVar: GlobalVarRepeat
  getFormatId: () => string
  getFormatData: () => object | null | undefined
  getShokumuFromOther?: () => object | null | undefined
  nextTick: () => Promise<void>
  onContinue: (trigger: RtRepeatChainTrigger) => void | Promise<void>
  onEnd: (trigger: RtRepeatChainTrigger) => void | Promise<void>
  /** advance 直前（未送信 close チェーン用） */
  onBeforeAdvance?: (trigger: RtRepeatChainTrigger) => void | Promise<void>
  /** 送客同意後など、count_repeat 上限に関係なく次 RT を表示する */
  onRequestOtherJob?: () => void | Promise<void>
}

export interface RtPopupRepeatChainController {
  /** チェーン開始時（初回 displayPopup 前）に count_repeat を 0 に戻す */
  beginChain: () => void
  resetCount: () => void
  /** 1 件分チェーンを進め、上限内なら次 RT を表示する（未送信 close 用） */
  advance: (trigger: RtRepeatChainTrigger) => Promise<RtRepeatChainAdvanceResult>
  /** 件数上限なしで次 RT を表示する（送客同意後サブボタン用） */
  requestOtherJobPopup: () => Promise<RtOtherJobPopupResult>
  getTotalDisplayMax: () => number
  getState: () => {
    countRepeat: number
    totalDisplayMax: number
    displayedCount: number
    canAdvance: boolean
  }
  isChainActive: () => boolean
}

/**
 * RT ポップアップ再表示チェーンの状態機械。
 * - advance(): 未送信 close 用（KW 上限 2/4 件）
 * - requestOtherJobPopup(): 送客同意後サブボタン用（上限なし）
 */
export function createRtPopupRepeatChain (
  options: CreateRtPopupRepeatChainOptions
): RtPopupRepeatChainController {
  let coalescing = false

  const resetCount = () => {
    options.globalVar.count_repeat = 0
  }

  const getTotalDisplayMax = () => getRtRepeatTotalDisplayMax(
    options.globalVar,
    options.getFormatId(),
    options.getFormatData(),
    options.getShokumuFromOther?.()
  )

  const getState = () => {
    const totalDisplayMax = getTotalDisplayMax()
    const displayedCount = 1 + options.globalVar.count_repeat
    return {
      countRepeat: options.globalVar.count_repeat,
      totalDisplayMax,
      displayedCount,
      canAdvance: displayedCount < totalDisplayMax
    }
  }

  const isChainActive = () => options.globalVar.count_repeat > 0

  const advance = async (trigger: RtRepeatChainTrigger): Promise<RtRepeatChainAdvanceResult> => {
    if (coalescing) return 'skipped'
    coalescing = true
    await options.nextTick()
    coalescing = false

    await options.onBeforeAdvance?.(trigger)

    options.globalVar.count_repeat++
    const nextTotalDisplay = 1 + options.globalVar.count_repeat
    const totalDisplayMax = getTotalDisplayMax()

    if (nextTotalDisplay <= totalDisplayMax) {
      await options.onContinue(trigger)
      return 'continued'
    }

    resetCount()
    await options.onEnd(trigger)
    return 'ended'
  }

  const requestOtherJobPopup = async (): Promise<RtOtherJobPopupResult> => {
    if (coalescing) return 'skipped'
    coalescing = true
    await options.nextTick()
    coalescing = false

    await options.onRequestOtherJob?.()
    return 'shown'
  }

  return {
    beginChain: resetCount,
    resetCount,
    advance,
    requestOtherJobPopup,
    getTotalDisplayMax,
    getState,
    isChainActive
  }
}

export interface RtPopupRepeatChainViewBindings {
  displayPopup: (opts?: { isRtRepeat?: boolean }) => void | Promise<void>
  getFormatId: () => string
  getFormatData: () => object | null | undefined
  getShokumuFromOther?: () => object | null | undefined
  getDisableRtCondition4Repeat: () => object | null | undefined
  setDisableRtCondition: (value: object) => void
  applyPendingToConditionSet: <T extends object>(conditionSet: T) => T
  beforeContinue?: (trigger: RtRepeatChainTrigger) => void | Promise<void>
  afterEnd?: (trigger: RtRepeatChainTrigger) => void | Promise<void>
  /** 省略時は beforeContinue + displayPopup({ isRtRepeat: true }) */
  requestOtherJobPopup?: () => void | Promise<void>
}

/** Options API 画面向け: createRtPopupRepeatChain の定型 wiring */
export function createRtPopupRepeatChainBindings (
  globalVar: GlobalVarRepeat,
  nextTick: () => Promise<void>,
  bindings: RtPopupRepeatChainViewBindings
): RtPopupRepeatChainController {
  return createRtPopupRepeatChain({
    globalVar,
    nextTick,
    getFormatId: bindings.getFormatId,
    getFormatData: bindings.getFormatData,
    getShokumuFromOther: bindings.getShokumuFromOther,
    onContinue: async (trigger) => {
      await bindings.beforeContinue?.(trigger)
      await bindings.displayPopup({ isRtRepeat: true })
    },
    onEnd: async (trigger) => {
      const restored = restoreRtConditionAfterRepeatEnd(
        bindings.getDisableRtCondition4Repeat(),
        bindings.applyPendingToConditionSet
      )
      if (restored) bindings.setDisableRtCondition(restored)
      await bindings.afterEnd?.(trigger)
    },
    onRequestOtherJob: async () => {
      if (bindings.requestOtherJobPopup) {
        await bindings.requestOtherJobPopup()
        return
      }
      await bindings.beforeContinue?.('close-without-submit')
      await bindings.displayPopup({ isRtRepeat: true })
    }
  })
}

/** Composition API 向けエイリアス（中身は createRtPopupRepeatChain と同一） */
export const useRtPopupRepeatChain = createRtPopupRepeatChain
